import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnInit,
  Optional,
  Type,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { BehaviorSubject } from 'rxjs';
import {
  FormOrchestratorTableData,
  FormTableColumns,
} from '../../models/table.models';
import { TableFormService } from '../../services/table-form/table-form.service';
import { WindowService } from '../../services/window.service';
import {
  ACTION_CELL_COMPONENT,
  DETAIL_COMPONENT,
} from '../../token/detail-token';
import { UiTableOrchestratorDirective } from '../table-orchestrator/table-orchestrator.directive';

@Directive({
  selector: '[bsFormOrchestrator]',
  exportAs: 'formOrchestrator',
})
export class UiTableFormOrchestratorDirective<T>
  extends UiTableOrchestratorDirective<T>
  implements OnInit
{
  @HostBinding('class.table-image') isTableImage!: boolean;
  @Input() formOrchestratorDisplayColumns!: FormTableColumns[];
  @Input() formOrchestratorData!: FormOrchestratorTableData<T>[];
  @Input() tableFormOrchestratorEventAction!: EventEmitter<T>;
  @Input() formOrchestratorIdPropertyName = 'id';
  @Input() keyTable!: string;
  columns!: string[];
  columnsType!: (
    | 'counter'
    | 'checkbox'
    | 'radio'
    | 'data'
    | 'text'
    | 'number'
    | 'image'
    | 'incrementor'
    | 'value-hover'
    | 'observation'
    | 'remove'
    | 'retirada'
    | 'desconto'
    | 'edit-client'
    | 'packaging-select'
  )[];
  columnIsClickable!: (
    | {
        highLightColor?: string | undefined;
        fontWeight?: string | undefined;
        cellIsClickable?: boolean;
        highlightColor?: string;
        url?: string[];
        queryParamsFn?: (element: any) => { [key: string]: any };
        clickableCellClickFn?: (event: MouseEvent, element: any) => void;
      }
    | undefined
  )[];

  formGroup: FormGroup<{
    [key: string]: FormControl<T>;
  }> = new FormGroup({});
  columnsPipeType: any;
  columnsPipeParams: any;

  constructor(
    @Optional()
    @Inject(DETAIL_COMPONENT)
    readonly formDetailComponentType: Type<
      T & {
        data: T;
        eventAction: EventEmitter<T>;
      }
    >,
    @Optional()
    @Inject(ACTION_CELL_COMPONENT)
    readonly formActionCellComponentRef: Type<
      T & {
        data: T;
        eventAction: EventEmitter<T>;
      }
    >,
    readonly formChangeDetectorRef: ChangeDetectorRef,
    readonly formWindowService: WindowService,
    readonly tableFormService: TableFormService<T>
  ) {
    super(
      formDetailComponentType,
      formActionCellComponentRef,
      formChangeDetectorRef,
      formWindowService
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.tableOrchestratorColumnsToDisplay =
      this.formOrchestratorDisplayColumns.map(({ columnName }) => columnName);

    this.columns = this.formOrchestratorDisplayColumns.map(
      (column) => column.columnName
    );

    this.columnsType = this.formOrchestratorDisplayColumns.map((column) => {
      if (column.columnType === 'image') {
        this.isTableImage = true;
      }

      return column.columnType;
    });

    this.columnsPipeType = this.formOrchestratorDisplayColumns.map(
      (column) => column.typePipe
    );

    this.columnsPipeParams = this.formOrchestratorDisplayColumns.map(
      (column) => column.typePipeParams
    );

    this.columnIsClickable = this.formOrchestratorDisplayColumns.map(
      (column) => column.columnIsClickable
    );

    this.tableOrchestratorDataSource = this.formOrchestratorData;

    if (!this.tableFormService.formGroups[this.keyTable]) {
      this.tableFormService.formGroups[this.keyTable] = new FormGroup({});
    }
    const hasControls = Object.keys(
      this.tableFormService.formGroups[this.keyTable]?.controls ?? []
    );
    if (hasControls.length >= 1) {
      hasControls.forEach((key) => {
        this.tableFormService.formGroups[this.keyTable].removeControl(key, {
          emitEvent: false,
        });
      });
    }
    const formArray = this.tableOrchestratorDataSource.map((_, index) => {
      const element = this.formOrchestratorData[index];
      const formGroupValues = element.formGroupValue;
      const formGroup = new FormGroup({});

      formGroupValues.reduce((formGroup, formGroupValue) => {
        const { formControlName, value, validators } = formGroupValue;

        const formControl = new FormControl<T>(value, validators);

        formGroup.addControl(formControlName, formControl, {
          emitEvent: false,
        });

        return formGroup;
      }, formGroup);

      return formGroup;
    });

    formArray.forEach((formGroup, index) => {
      const tableOrchestratorData = this
        .tableOrchestratorDataSource as unknown as {
        [key: string]: T;
      }[];

      const element = tableOrchestratorData[index];

      this.tableFormService.formGroups[this.keyTable].addControl(
        `${element[this.formOrchestratorIdPropertyName]}`,
        formGroup,
        {
          emitEvent: false,
        }
      );
    });
    const data = this.tableOrchestratorDataSource[0] as any;

    if(data?.observacao || data?.obs){
      this.tableFormService.tableDataEditeCart$$ = new BehaviorSubject(
        this.tableOrchestratorDataSource
      );
    }

    this.tableFormService.tableData$$ = new BehaviorSubject(
      this.tableOrchestratorDataSource
    );

    this.tableFormService.tableData$$.subscribe((updateData) => {
      this.matTableDataSource = new MatTableDataSource(updateData);
      this.changeDetectorRef.detectChanges();
    });
  }

  getControl(index: number, columnName: string) {
    const control = this.getFormControl(index, columnName);
    if (!control) this.getFormControl(index, columnName);

    return control as FormControl<T>;
  }

  getFormControl(
    index: number,
    columnName: string
  ): FormControl<T> | undefined {
    const stringIndex = index.toString();

    const column = this.formOrchestratorDisplayColumns.find(
      (column) => column.columnName === columnName
    );

    const formGroup =
      this.tableFormService.formGroups[this.keyTable].get(stringIndex);
    if (column && column.columnFormControlName && formGroup) {
      return formGroup.get(column.columnFormControlName);
    }

    return new FormControl<T>();
  }
}
